import type { LucideIcon } from "lucide-react";
import { Globe, MonitorSmartphone, Server, Wrench } from "lucide-react";

import { Card } from "@/components/ui/card";
import type { SkillsPageSection } from "@/types/site";

interface SkillsSectionProps {
  content: SkillsPageSection;
}

const skillIcons: Record<string, string | LucideIcon> = {
  "App Store": "/skills/appstore.svg",
  Claude: "/skills/claude.svg",
  Cloudflare: "/skills/cloudflare.svg",
  Codex: "/skills/codex.svg",
  CoreDNS: Server,
  CSS: "/skills/css.svg",
  Figma: "/skills/figma.svg",
  Git: "/skills/git.svg",
  GitHub: "/skills/github.svg",
  "Google Gemini": "/skills/googlegemini.svg",
  HTML: "/skills/html5.svg",
  JavaScript: "/skills/javascript.svg",
  Kubernetes: "/skills/kubernetes.svg",
  Markdown: "/skills/markdown.svg",
  "Node.js": "/skills/nodedotjs.svg",
  Notion: "/skills/notion.svg",
  PostgreSQL: "/skills/postgresql.svg",
  "REST APIs": Globe,
  React: "/skills/react.svg",
  Supabase: "/skills/supabase.svg",
  Swift: "/skills/swift.svg",
  SwiftUI: MonitorSmartphone,
  "Tailwind CSS": "/skills/tailwindcss.svg",
  TypeScript: "/skills/typescript.svg",
  Vite: "/skills/vite.svg",
  Xcode: "/skills/xcode.svg",
};

function getSkillIcon(skill: string): string | LucideIcon {
  return skillIcons[skill] ?? Wrench;
}

/**
 * Renders grouped technical skills for the dedicated skills page.
 */
export function SkillsSection({ content }: SkillsSectionProps): JSX.Element {
  return (
    <section className="pb-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-10 max-w-3xl">
          <p className="text-sm leading-7 text-spotify-silver">
            {content.description}
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {content.groups.map((group) => (
            <Card
              className="border border-border/20 bg-spotify-surface/70 p-7"
              key={group.title}
            >
              <div className="space-y-5">
                <h2 className="text-2xl font-semibold text-foreground">
                  {group.title}
                </h2>
                <ul className="flex flex-wrap gap-3">
                  {group.items.map((item) => {
                    const iconOrPath = getSkillIcon(item);
                    const isString = typeof iconOrPath === "string";
                    const Icon = !isString ? iconOrPath : null;

                    return (
                      <li
                        className="inline-flex items-center gap-2 rounded-full-pill border border-border/20 bg-spotify-surface-light px-4 py-2 text-sm font-medium text-foreground"
                        key={item}
                      >
                        {isString ? (
                          <span
                            aria-hidden="true"
                            className="block size-4 shrink-0 bg-primary"
                            style={{
                              WebkitMaskImage: `url("${iconOrPath}")`,
                              WebkitMaskPosition: "center",
                              WebkitMaskRepeat: "no-repeat",
                              WebkitMaskSize: "contain",
                              maskImage: `url("${iconOrPath}")`,
                              maskPosition: "center",
                              maskRepeat: "no-repeat",
                              maskSize: "contain",
                            }}
                          />
                        ) : (
                          Icon && (
                            <Icon
                              aria-hidden="true"
                              className="size-4 shrink-0 text-primary"
                            />
                          )
                        )}
                        <span>{item}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
